import React, { useRef, useEffect, useState, Suspense } from 'react';
import { HiUpload } from 'react-icons/hi';
import ProductFeedback from './components/ProductFeedback';
import StructuredMessage from './components/StructuredMessage';
import MenuSheet from './components/MenuSheet';
import ClimateModal from './components/ClimateModal';
import ProgressTrackerPlaceholder from './components/ProgressTrackerPlaceholder';
import ProductSaveButtons from './components/ProductSaveButtons';
import ProductSaveFromAnalysis from './components/ProductSaveFromAnalysis';
import ProductRecommendations from './components/ProductRecommendations';
import ProductAnalysisCard from './components/ProductAnalysisCard';
import ProductAnalysisSummaryCard from './components/ProductAnalysisSummaryCard';
// Skin analysis card: Prototype A (polished classic) selected 2026-06-04.
// Prototype B (carousel) remains in SkinAnalysisCard.jsx for the preview
// route comparison; swap this import to flip the in-chat card.
import SkinAnalysisCard from './components/SkinAnalysisCardClassic';
import LandingHero from './components/LandingHero';
import OutcomeLanding from './components/OutcomeLanding';
import ShadePhotoReview from './components/ShadePhotoReview';
import InspirationPersonaliseCta from './components/InspirationPersonaliseCta';
import ScanIntakeModal from './components/ScanIntakeModal';
import SuggestedQuestions from './components/SuggestedQuestions';
import ProfileCompletionNudge from './components/ProfileCompletionNudge';
import ChatHistoryDrawer from './components/ChatHistoryDrawer';
import { isGuidedCaptureEnabled } from './skin-capture/web/flag';
import { TypingFact } from './components/WaitingEntertainment';
import ServiceUnavailableMessage from './components/ServiceUnavailableMessage';
import { getChatRequestUrl } from './config';
import { supabase } from './supabase';
import { GUEST_PROFILE_KEY } from './components/FirstTimeOverview';
import { stripLeadingToolCallJson } from './utils/stripLeadingToolCallJson';
import { normalizeStructuredContent } from './utils/structuredReply';
import { IMAGE_ROLES, createImageJourney } from './utils/imageJourney';
import { isScrollNearBottom, scrollChatToBottom } from './utils/chatResilience';
import { isOutcomeOnboardingEnabled } from './utils/outcomeOnboarding';
import { useAuth } from './hooks/useAuth';
import {
  trackProductSaved,
  trackProductAnalyzed,
  trackProductComparison,
  trackMenuOpened,
  trackProductCatalogViewed,
  trackPhotoUpload,
  trackMultiProductAttempt,
  trackOutcomeJourneyStarted,
  trackOutcomeOnboardingViewed,
  trackOutcomeSelected,
  trackJourneyNextActionSelected,
  trackClimateModalShown, trackClimateSaved, trackClimateSignInClicked, trackClimateDismissed,
} from './utils/analytics';

const journeyActionFor = (message) => message?.journeyAction
  || (message?.text && typeof message.text === 'object' ? message.text.journeyAction : null);

const journeyContextFor = (message) => message?.journeyContext
  || (message?.text && typeof message.text === 'object' ? message.text.journeyContext : null)
  || {};

// Lazy so MediaPipe's ~1.4MB WASM-loader JS is code-split into its own chunk and
// only downloads when the user actually opens the guided camera (spec: "lazy-load
// on entering the scan screen"). Keeps it out of everyone's initial bundle.
const GuidedCapture = React.lazy(() => import('./skin-capture/web/GuidedCapture'));

// Default suggested questions when chat is empty (before backend suggestions load)
const DEFAULT_EMPTY_SUGGESTED_QUESTIONS = [
  { id: 'empty-1', text: "What's my skin type?", category: 'general' },
  { id: 'empty-2', text: 'Recommend a foundation shade', category: 'general' },
  { id: 'empty-3', text: 'Build me a routine', category: 'general' },
];

const DESKTOP_BREAKPOINT = 769;

// The skin/scan backend sends `skinAnalysis` as an OBJECT {type, analysis} where
// `analysis` is the sectioned markdown — the old gate checked `typeof === 'string'`
// and missed it, so the scan fell back to prose. Normalise to the markdown string
// (object.analysis, a bare string, or the legacy `## Skin Tone Analysis` in text)
// so the professional card renders. Returns null when there's no skin read.
const skinReadMarkdown = (msg) => {
  const sa = msg && msg.skinAnalysis;
  const md = typeof sa === 'string' ? sa : (sa && typeof sa.analysis === 'string' ? sa.analysis : '');
  if (md && md.includes('## Skin Tone')) return md;
  if (typeof (msg && msg.text) === 'string' && msg.text.includes('## Skin Tone Analysis')) return msg.text;
  return null;
};

// The image-upload flow appends a canned analysis instruction to the message
// sent to the backend. That's a prompt, not something the user should see in
// their own bubble — strip it so an upload shows the user's words (or just the
// photo). Keeps any text the user actually typed before it.
const stripImageBoilerplate = (text) => {
  if (typeof text !== 'string') return text;
  return text
    .replace(/\n*\s*I(?:'|’)?ve uploaded an image[\s\S]*$/i, '')
    .replace(/\n*\s*I uploaded a photo for analysis\.?\s*$/i, '')
    .trim();
};


const ChatUI = ({ messages, input, setInput, sendMessage, uploadImage, uploadImages, uploadProductImage, uploadPhoto, uploadCaptureImage, showAnalysisTypeChoice, isLoading, streamStatus = '', onRefineResults, onRequestUpgrade, uploadedImages, analysisProgress, showAnalyzedPhotos, saveProductToBackend, getSavedProductsFromBackend, userId, isAnonymous = false, onShowSignIn, onPersistentSignIn, suggestedQuestions = [], onSuggestedQuestionClick, suggestionsDismissed = false, onDismissSuggestions, onShowSuggestions, showProfileNudge = false, profileCompleteness = 0, missingFields = [], onProfileNudgeDismiss, onCompleteProfile, onProfileSave, chatList = [], currentSessionId, onNewChat, onSwitchChat, autoTriggerUpload = false, onAutoTriggerUploadHandled, imageConsent, requestConsent, onImageConsentChange, onIntakeCollected, onReadFeedback, onCorrectionSubmit, onShadeMatchesRequested, scanIntakeKnown = null, resolveScanIntake = null, openMenuAt = null, onOpenMenuAtHandled, onGatedCompose = null, gateActive = false }) => {
  const { getAuthSession } = useAuth();
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT);

  // A brand-new chat (nothing but the seeded welcome message) shows the landing
  // hero instead of the message list + the old "Upload a selfie" empty state.
  const [directChatSessionId, setDirectChatSessionId] = useState(null);
  // Covers the gap between the guided camera closing and the normal analysis
  // request entering its loading state.
  const [scanHandoff, setScanHandoff] = useState(false);
  const directChatKey = currentSessionId || 'new-conversation';
  const isWelcomeOnly =
    messages.length === 1 && messages[0]?.sender === 'assistant' && !!messages[0]?.isWelcome && !isLoading && !scanHandoff;
  const enteredDirectChat = isWelcomeOnly && directChatSessionId === directChatKey;
  const isLandingState = isWelcomeOnly && !enteredDirectChat;

  useEffect(() => {
    const check = () => {
      const wide = window.innerWidth >= DESKTOP_BREAKPOINT;
      setIsDesktop(wide);
    };
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);

  // URL-prefilled questions change input state without firing a textarea change event.
  useEffect(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = input?.trim() ? `${Math.min(textarea.scrollHeight, 120)}px` : '20px';
    textarea.style.overflowY = textarea.scrollHeight > 120 ? 'auto' : 'hidden';
  }, [input, gateActive, isLandingState]);
  // Separate input for direct camera capture. Android needs the `capture`
  // attribute to offer Take Photo; iOS users still get the same native sheet.
  const cameraInputRef = useRef(null);
  // Where the pending photo came from, for analytics. Both file inputs share
  // handleFileChange, so we can't tell camera from library in the handler —
  // stamp it here at the point the user chose the path. Guided scan sets its
  // own source in GuidedCapture's onCapture.
  const uploadSourceRef = useRef('library');
  // React state does not update before the native picker can return. Keep the
  // exact action that opened it in a ref so a normal photo can never inherit
  // the previous "face scan" selection.
  const pendingUploadIntentRef = useRef('photo');
  const outcomeUploadRef = useRef(null);
  const [pendingShadePhoto, setPendingShadePhoto] = useState(null);
  const [loadingPurpose, setLoadingPurpose] = useState('chat');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuInitialView, setMenuInitialView] = useState('menu');
  const showOutcomeLanding = isOutcomeOnboardingEnabled()
    || new URLSearchParams(window.location.search).get('journey') === 'colour-makeup';
  const outcomeViewTrackedRef = useRef(null);
  useEffect(() => {
    if (!showOutcomeLanding || !isLandingState || outcomeViewTrackedRef.current === currentSessionId) return;
    outcomeViewTrackedRef.current = currentSessionId;
    trackOutcomeOnboardingViewed();
  }, [showOutcomeLanding, isLandingState, currentSessionId]);
  // "See full formula analysis" CTA on a product turn: opens the Product Analysis
  // section — same entry point the skin-read card's "Analyse a product" uses.
  // Deliberately conservative: a CTA only appears when the section genuinely
  // beats the chat answer. No CTA is better than one that leads somewhere thinner.
  const ctaForTurn = (msg) => {
    if (!msg) return null;
    const body = typeof msg.text === 'object' && msg.text
      ? [msg.text.advice, msg.text.answer, msg.text.routine, msg.text.products].filter(Boolean).join(' ')
      : String(msg.text || '');
    const t = body.toLowerCase();
    // Routine CTA rides at the top of the Routine tab whenever this turn actually
    // HAS a routine tab (Sofia 2026-08-27). We key off the same signal
    // StructuredMessage uses to show the tab — content.routine having content —
    // NOT a text heuristic, and NOT after the shade guard (a foundation turn can
    // still carry a routine, and it was being suppressed). This is the deliberate
    // reversal of a637a30, which narrowed the CTA to "my/your routine" phrasing
    // and made it vanish from almost every answer.
    const _r = msg.text && typeof msg.text === 'object' ? msg.text.routine : null;
    const hasRoutineTab = !msg.productContext && (
      (typeof _r === 'string' && _r.trim() !== '') ||
      (Array.isArray(_r) && _r.length > 0) ||
      (_r && typeof _r === 'object' && !Array.isArray(_r) && Object.keys(_r).length > 0)
    );
    if (hasRoutineTab) {
      // An empty saved routine has nothing to "open", so invite them to scan.
      let hasRoutine = true;
      try { hasRoutine = localStorage.getItem('sonmi_routine_filled') !== '0'; } catch (_e) {}
      return {
        label: hasRoutine ? 'Open My Routine' : 'Scan my routine',
        onClick: openRoutine,
        placement: 'routine',
      };
    }
    // A single-product analysis turn -> the full formula breakdown (Advice tab).
    if (msg.productContext) {
      return { label: 'See full formula analysis', onClick: openProductAnalysis, placement: 'advice' };
    }
    // Otherwise (pure shade/makeup, or plain advice with no routine tab): no CTA.
    return null;
  };

  const openRoutine = () => {
    setMenuInitialView('routine');
    setIsMenuOpen(true);
  };

  const openProductAnalysis = () => {
    setMenuInitialView('product-analysis');
    setIsMenuOpen(true);
  };

  const openProductComparison = () => {
    setMenuInitialView('product-comparison');
    setIsMenuOpen(true);
  };

  const openProductHub = () => {
    setMenuInitialView('catalog');
    setIsMenuOpen(true);
  };

  // Deep link from email: /?open=menu drops straight into the product menu,
  // where Sign in and Analyse a product both sit. Without this a campaign CTA
  // labelled "Analyse a product" lands on the chat screen and the reader has to
  // go hunting, and anyone who already has an account stays anonymous because
  // sign in is behind the menu button.
  //
  // Allowlisted rather than passed through: `open` comes from a URL a stranger
  // can edit, and MenuSheet takes it as the view to render.
  useEffect(() => {
    const view = new URLSearchParams(window.location.search).get('open');
    // 'signin' and 'climate' are handled elsewhere, not by MenuSheet.
    const ALLOWED = ['menu', 'product-analysis', 'personal-profile', 'routine', 'progress'];
    if (!view || !ALLOWED.includes(view)) return;
    setMenuInitialView(view);
    setIsMenuOpen(true);
  }, []);

  // Deep link from email: /?ask=I%20live%20in%20 drops the reader into chat with
  // the message half written, so a CTA like "Set your climate" is one step
  // rather than a hand-off to an empty box.
  //
  // Prefills the input, never sends. The text comes from a URL a stranger can
  // edit, so nothing acts on it until the person reads it and presses send
  // themselves. Trimmed to a sane length so a crafted link cannot paste an essay.
  useEffect(() => {
    const ask = new URLSearchParams(window.location.search).get('ask');
    if (!ask || typeof setInput !== 'function') return;
    setInput(ask.slice(0, 140));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The climate modal, opened only by /?open=climate from the weather email.
  //
  // Once per device, and the param is stripped as soon as it fires, so a
  // refresh cannot re-open it and a forwarded link cannot carry it to someone
  // who never got the email. Anyone arriving from anywhere else never has the
  // param, so no other entry point is affected.
  const [showClimateModal, setShowClimateModal] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') !== 'climate') return;
    if (localStorage.getItem('sonmi_climate_asked') === '1') return;
    setShowClimateModal(true);
    trackClimateModalShown();
    params.delete('open');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
  }, []);

  // Persist the climate answer. onProfileSave is NOT a save function, it only
  // moves the profile-completeness nudge, so calling it here saved nothing and
  // crashed the app by passing it a payload with no `completeness`.
  //
  // Signed in: straight to user_profiles, the same columns PersonalProfile
  // writes. Anonymous: the guest profile in localStorage, which PersonalProfile
  // already reads back and merges on sign-up.
  const saveClimate = async ({ climate, waterType }) => {
    try {
      if (userId && !isAnonymous) {
        await supabase.from('user_profiles').upsert(
          { user_id: userId, climate, water_type: waterType || null,
            updated_at: new Date().toISOString() },
          { onConflict: 'user_id' }
        );
      } else {
        const prev = JSON.parse(localStorage.getItem(GUEST_PROFILE_KEY) || '{}');
        localStorage.setItem(GUEST_PROFILE_KEY,
          JSON.stringify({ ...prev, climate, water_type: waterType || null }));
      }
      // Also recorded as an event, which is what makes a guest's answer visible
      // server-side. The profile copy above stays local until they sign up.
      trackClimateSaved({ climate, waterType, anonymous: !userId || isAnonymous });
      climateSaved.current = true;
    } catch (e) {
      // Never let a failed write take the modal down with it.
      console.error('[climate] save failed', e);
    }
  };

  const climateSaved = useRef(false);

  const closeClimateModal = () => {
    trackClimateDismissed({ saved: climateSaved.current });
    localStorage.setItem('sonmi_climate_asked', '1');
    setShowClimateModal(false);
  };
  const [currentView, setCurrentView] = useState('chat'); // 'chat', 'progress'
  // Tracks when a skin analysis was triggered from the Personal Profile "Start" button.
  // Shows a context banner so the user knows to check their profile for updated fields.
  const [skinAnalysisFromProfile, setSkinAnalysisFromProfile] = useState(false); // analysis in progress
  const [showProfileResultBanner, setShowProfileResultBanner] = useState(false); // analysis complete
  const prevIsLoadingRef = useRef(false);
  // Tracks when the image-guidance modal opened — used to ignore the stray
  // touch/click that propagates through after a menu→modal transition on iOS,
  // which would otherwise immediately dismiss the modal (the "Start button does
  // nothing" symptom users have reported repeatedly).
  const guidanceOpenedAt = useRef(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // True while the soft keyboard is open (mobile web). Drives hiding the
  // scroll-to-bottom arrow so it can't land on the Send button.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Product storage state
  const [savedProducts, setSavedProducts] = useState({
    favourites: [],
    current: [],
    wishlist: [],
    custom1: [],
    custom2: []
  });

  // Cache gate for the My Products menu button. We pre-fetch on mount
  // (preloadSavedProducts) so by the time the user opens the menu, the
  // local state is already populated. Without this ref, tapping My Products
  // re-runs the entire fetch + per-product signed URL refresh every time —
  // Sofia hit this on murex 2026-05-29: "loads anew and just takes a really
  // long time" on every navigation back. Local state stays in sync with
  // optimistic writes from saveProduct / deleteProduct, so a 60s TTL on the
  // re-fetch is safe; the worst case is a 60s lag picking up writes from
  // another device.
  const SAVED_PRODUCTS_TTL_MS = 60 * 1000;
  const lastSavedProductsFetchRef = useRef(0);
  
  const [customListNames, setCustomListNames] = useState({
    custom1: 'My Custom List 1',
    custom2: 'My Custom List 2'
  });

  // Refresh any Supabase Storage signed URLs in saved products (they expire after 7 days)
  const refreshProductImages = async (productsMap) => {
    const refreshUrl = async (url) => {
      if (!url || typeof url !== 'string') return url;
      if (!url.includes('supabase.co/storage')) return url;
      const match = url.match(/\/object\/sign\/images\/(.+?)\?/);
      if (!match) return url;
      try {
        const { data, error } = await supabase.storage
          .from('images')
          .createSignedUrl(match[1], 604800); // 7-day TTL
        if (!error && data?.signedUrl) return data.signedUrl;
      } catch (_) {}
      return url;
    };

    const refreshProduct = async (p) => {
      const freshUrl = await refreshUrl(p.image_url || p.image || p.image_base64 || '');
      if (!freshUrl || freshUrl === (p.image_url || p.image || p.image_base64)) return p;
      // Write the refreshed URL back into whichever field was populated
      if (p.image_url) return { ...p, image_url: freshUrl };
      if (p.image) return { ...p, image: freshUrl };
      return p;
    };

    const result = {};
    for (const [list, items] of Object.entries(productsMap)) {
      result[list] = Array.isArray(items)
        ? await Promise.all(items.map(refreshProduct))
        : items;
    }
    return result;
  };

  // Load custom list names from Supabase on mount / user change
  useEffect(() => {
    if (!userId) return;
    const loadListNames = async () => {
      const { data } = await supabase
        .from('user_lists')
        .select('type, name')
        .eq('user_id', userId)
        .in('type', ['custom1', 'custom2']);
      if (data && data.length > 0) {
        const names = {};
        data.forEach(row => { names[row.type] = row.name; });
        setCustomListNames(prev => ({ ...prev, ...names }));
      }
    };
    loadListNames();
  }, [userId]);

  // Pre-fetch saved products on mount so enrichedProducts is populated
  // for SkinRoutine even if the user never opens My Products.
  useEffect(() => {
    if (!userId || isAnonymous) return;
    const preloadSavedProducts = async () => {
      try {
        const products = await getSavedProductsFromBackend(userId);
        if (products) {
          const refreshed = await refreshProductImages(products);
          setSavedProducts(refreshed);
          lastSavedProductsFetchRef.current = Date.now();
        }
      } catch (_) {}
    };
    preloadSavedProducts();
  }, [userId]);
  
  // Product save from analysis state
  const [showProductSaveFromAnalysis, setShowProductSaveFromAnalysis] = useState(false);
  const [currentAnalysisForSave, setCurrentAnalysisForSave] = useState(null);
  const [currentProductImageForSave, setCurrentProductImageForSave] = useState(null);

  // Load saved products from backend on component mount
  // ❌ REMOVED: Don't load products on mount - only load when user opens product catalog
  // This prevents unnecessary database calls during normal chat conversations

  // Function to save a product to a specific category
  const saveProduct = async (product, category) => {
    if (!userId) {
      console.error('No userId - cannot save product');
      return;
    }
    const listCategory = product.listCategory || category;
    try {
      const savedProduct = await saveProductToBackend(product, listCategory, userId);
      if (savedProduct) {
        setSavedProducts(prev => ({
          ...prev,
          [listCategory]: [...(prev[listCategory] || []), { ...product, id: savedProduct.id }]
        }));
        trackProductSaved({
          name: product.name,
          brand: product.brand,
          category: product.category,
          listName: listCategory
        });
      } else {
        // Fallback to local state only if backend fails
        setSavedProducts(prev => ({
          ...prev,
          [listCategory]: [...(prev[listCategory] || []), product]
        }));
      }
    } catch (error) {
      console.error('Error saving product:', error);
      // Still update local state so the UI doesn't appear broken
      setSavedProducts(prev => ({
        ...prev,
        [listCategory]: [...(prev[listCategory] || []), product]
      }));
    }
  };

  // Function to update a product
  const updateProduct = async (productId, updatedProduct) => {
    try {
      
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }
      
      const response = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: userId,  // Required for backend to identify user
          updateProduct: {
            id: productId,
            ...updatedProduct
          }
        })
      });
      
      if (response.status === 401) {
        throw new Error('Session expired. Please refresh the page and sign in again.');
      }
      
      if (response.ok) {
        const result = await response.json();
        
        // Use the updated product from backend response (includes all fields like image)
        const updatedProductFromBackend = result.product || result.updatedProduct || updatedProduct;
        
        // Update local state with the full product from backend
        setSavedProducts(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(category => {
            if (Array.isArray(newState[category])) {
              newState[category] = newState[category].map(product => 
                product.id === productId ? { ...product, ...updatedProductFromBackend } : product
              );
            }
          });
          return newState;
        });
        
        return result;
      } else {
        console.error('❌ Failed to update product:', response.status);
        throw new Error('Failed to update product');
      }
    } catch (error) {
      console.error('❌ Error updating product:', error);
      throw error;
    }
  };

  // Function to delete a product
  const deleteProduct = async (productId) => {
    try {
      
      if (!userId) {
        console.error('❌ No userId - cannot delete product');
        throw new Error('Authentication required');
      }
      
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }
      
      const response = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          deleteProduct: true,
          productId: productId,
          userId: userId
        })
      });

      if (response.status === 401) {
        throw new Error('Session expired. Please refresh the page and sign in again.');
      }

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      const result = await response.json();
      
      if (result.success) {
        
        // Update frontend state - remove product from all categories
        setSavedProducts(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(category => {
            updated[category] = (updated[category] || []).filter(p => p.id !== productId);
          });
          return updated;
        });
      } else {
        throw new Error(result.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('❌ Error deleting product:', error);
      throw error;
    }
  };

  // Function to create a new custom list.
  //
  // History: this used to fire-and-forget the Supabase upsert (no await,
  // no .then, no .catch). The DB CHECK constraint on user_lists.type was
  // 'current'|'wishlist'|'custom' but the frontend writes 'custom1'/'custom2',
  // so every write failed with Postgres error 23514. Local state updated, the
  // user saw the list, the DB had zero rows, the next refresh wiped it.
  //
  // The CHECK constraint was relaxed on 2026-05-29 (migration
  // user_lists_allow_custom1_custom2_types). This function now awaits the
  // write and surfaces errors so the next class of bug can't go silent.
  const createCustomList = async (listName) => {
    let listKey = null;
    if (customListNames.custom1 === 'My Custom List 1' && (savedProducts.custom1?.length ?? 0) === 0) {
      listKey = 'custom1';
    } else if (customListNames.custom2 === 'My Custom List 2' && (savedProducts.custom2?.length ?? 0) === 0) {
      listKey = 'custom2';
    }
    if (!listKey) return null;
    setCustomListNames(prev => ({ ...prev, [listKey]: listName }));
    if (userId) {
      try {
        const { error } = await supabase.from('user_lists').upsert(
          { user_id: userId, type: listKey, name: listName },
          { onConflict: 'user_id,type' }
        );
        if (error) {
          console.error(`❌ createCustomList: failed to persist '${listKey}'='${listName}' for user ${userId}:`, error);
          // Optimistic local state stays — user can keep using the list this
          // session, but warn them it won't survive refresh. We don't roll
          // back the local rename: the alternative (yanking the list out
          // mid-flow) is more disruptive than the eventual-disappear.
          if (typeof window !== 'undefined' && window?.alert) {
            window.alert(`Couldn't save list "${listName}". It may not persist after refresh. (${error.message || error.code || 'unknown error'})`);
          }
        } else {
          console.log(`✅ createCustomList: persisted '${listKey}'='${listName}'`);
        }
      } catch (e) {
        console.error(`❌ createCustomList: exception during upsert for '${listKey}':`, e);
      }
    }
    return listKey;
  };

  // Function to rename a custom list. Same fire-and-forget bug history as
  // createCustomList — awaited + surfacing errors now.
  const renameCustomList = async (listKey, newName) => {
    setCustomListNames(prev => ({ ...prev, [listKey]: newName }));
    if (userId) {
      try {
        const { error } = await supabase.from('user_lists').upsert(
          { user_id: userId, type: listKey, name: newName },
          { onConflict: 'user_id,type' }
        );
        if (error) {
          console.error(`❌ renameCustomList: failed to persist rename of '${listKey}' to '${newName}' for user ${userId}:`, error);
          if (typeof window !== 'undefined' && window?.alert) {
            window.alert(`Couldn't save rename to "${newName}". It may not persist after refresh. (${error.message || error.code || 'unknown error'})`);
          }
        } else {
          console.log(`✅ renameCustomList: persisted '${listKey}'='${newName}'`);
        }
      } catch (e) {
        console.error(`❌ renameCustomList: exception during upsert for '${listKey}':`, e);
      }
    }
  };

  // Function to save product from AI analysis
  const handleSaveProductFromAnalysis = async (productData) => {
    try {
      if (!userId) {
        console.error('❌ No userId - cannot save product from analysis');
        return;
      }
      await saveProductToBackend(productData, 'current', userId);
      
      // Don't refresh products here - they'll load when user opens product catalog
      
      setShowProductSaveFromAnalysis(false);
      setCurrentAnalysisForSave(null);
      setCurrentProductImageForSave(null);
    } catch (error) {
      console.error('Error saving product from analysis:', error);
    }
  };

  // Function to save product from recommendations
  const saveProductFromRecommendations = async (product, category) => {
    try {
      // Don't hard-return on a falsy `userId` prop — that was silently dropping
      // heart-saves. saveProductToBackend now pulls a fresh session itself and
      // returns the saved product (or null on failure) so the caller can reflect it.
      return await saveProductToBackend(product, category, userId);
      // (products refresh lazily when the user opens the catalog)
    } catch (error) {
      console.error('❌ Error saving product from recommendations:', error);
      return null;
    }
  };

  // Function to continue chat with product context (or start skin analysis from profile)
  const handleContinueToChat = (productData) => {
    // Close the menu first
    setIsMenuOpen(false);

    if (productData && productData.startSkinAnalysis) {
      // Mark that this analysis was initiated from the profile page so we can
      // show a "view your profile" banner once the result arrives.
      setSkinAnalysisFromProfile(true);
      setShowProfileResultBanner(false);
      setGuidanceMode('face');
      // Stamp the open time so the backdrop dismiss handler can ignore the
      // stray click/touch that propagates through from the Start tap when the
      // menu closes and the modal mounts in the same frame (iOS).
      guidanceOpenedAt.current = Date.now();
      setShowImageGuidance(true);
      return;
    }

    // 2026-06-11: "Ask a question" from the comparison results page. Menu is
    // already closed above, so the user lands in chat; prefill a question that
    // references the products they compared so the follow-up relates to it.
    if (productData?.comparison) {
      const names = (productData.comparison.productNames || []).filter(Boolean);
      if (names.length >= 2) {
        setInput(`About comparing ${names.slice(0, 2).join(' and ')}: `);
      } else if (names.length === 1) {
        setInput(`About the ${names[0]} I just compared: `);
      }
      return;
    }

    // Summary card was already added to chat when analysis completed; just prefill input and go to chat
    if (productData?.product?.name) {
      setInput(`About the ${productData.product.name} I just analysed: `);
    }
  };

  // Function to delete a custom list
  const deleteCustomList = async (listKey) => {
    setSavedProducts(prev => ({ ...prev, [listKey]: [] }));
    const defaultName = listKey === 'custom1' ? 'My Custom List 1' : 'My Custom List 2';
    setCustomListNames(prev => ({ ...prev, [listKey]: defaultName }));
    if (userId) {
      // Delete the list name record
      await supabase.from('user_lists').delete().eq('user_id', userId).eq('type', listKey);
      // Also remove all products in that list from saved_products
      await supabase.from('saved_products').delete().eq('user_id', userId).eq('list_category', listKey);
    }
  };

  // Pin the app shell to the VISUAL viewport. On iOS the soft keyboard shrinks
  // AND shifts the visual viewport down while the layout viewport stays full; we
  // mirror height + offsetTop onto --app-height / --app-top so .chat-container
  // follows the visible area exactly. The composer is an in-flow flex child, so
  // it rides the bottom of that area with zero keyboard arithmetic. This
  // replaces the old --vh / --kb scheme that double-accounted the keyboard
  // (app sized to the visual viewport AND a fixed composer lifted by --kb),
  // which left a dead band above the keyboard on iOS.
  useEffect(() => {
    const updateViewport = () => {
      const vv = window.visualViewport;
      const height = vv?.height || window.innerHeight;
      const offsetTop = vv?.offsetTop || 0;
      document.documentElement.style.setProperty('--app-height', `${Math.round(height)}px`);
      document.documentElement.style.setProperty('--app-top', `${Math.round(offsetTop)}px`);
      // --vh is still consumed by the guided-capture overlay (GuidedCapture.css),
      // so keep it in sync even though the chat shell no longer uses it.
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
      // Keyboard-open heuristic — only used to hide the scroll-to-bottom FAB. A
      // real keyboard eats >150px of layout-viewport height; browser-chrome
      // resizes are smaller and correctly ignored.
      const overlap = window.innerHeight - height - offsetTop;
      setKeyboardOpen(overlap > 150);
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    // iOS shifts the visual viewport WITHOUT firing resize when the keyboard
    // opens and the page pans under it, so listen for scroll too.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
    }

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport);
        window.visualViewport.removeEventListener('scroll', updateViewport);
      }
    };
  }, []);

  // OPTIMIZED: Scroll detection with throttling for better performance
  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      // Throttle scroll events to improve performance
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (messagesContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
          const isNearBottom = isScrollNearBottom({ scrollTop, scrollHeight, clientHeight });
          isNearBottomRef.current = isNearBottom;
          setShowScrollButton(!isNearBottom);
        }
      }, 100); // Throttle to 100ms
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      // Check initial state
      handleScroll();
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      };
    }
  }, []);

  const [showImageGuidance, setShowImageGuidance] = useState(false);
  const [guidanceMode, setGuidanceMode] = useState('face'); // 'face' | 'photo' | 'product'
  // The sheet asks what you want to DO, not what your photo IS. 'choose' is the
  // two-entry-point step; 'scanTips' is the guided-scan prep that keeps its tips.
  const [guidanceStep, setGuidanceStep] = useState('choose');
  // Attach-then-type: a product photo staged in the composer as a chip so the
  // user can add a question before it's sent. { file, previewUrl } or null.
  const [pendingAttachment, setPendingAttachment] = useState(null);
  // Guided skin-capture (feat/skin-capture, v0). Flag-gated + additive: when on,
  // the selfie "Take photo" path opens the live guided camera instead of the
  // native camera roll input. Everything else is untouched.
  const [showGuidedCapture, setShowGuidedCapture] = useState(false);
  // Post-capture intake: hold the captured photo(s) while we ask age +
  // sensitivities, then run ONE analysis with the answers included.
  const [pendingScan, setPendingScan] = useState(null); // { file, angles }
  const previousSessionIdRef = useRef(currentSessionId);

  // A chat switch is a hard UI boundary. Close sheets and discard staged input
  // so a product overlay, photo or chooser state cannot bleed into the next chat.
  useEffect(() => {
    const previous = previousSessionIdRef.current;
    previousSessionIdRef.current = currentSessionId;
    if (!previous || !currentSessionId || previous === currentSessionId) return;
    setIsHistoryOpen(false);
    setIsMenuOpen(false);
    setMenuInitialView('menu');
    setCurrentView('chat');
    setShowImageGuidance(false);
    setShowGuidedCapture(false);
    setPendingScan(null);
    setPendingAttachment(null);
    setPendingShadePhoto(null);
    outcomeUploadRef.current = null;
    setShowProfileResultBanner(false);
    setSkinAnalysisFromProfile(false);
    setLoadingPurpose('chat');
  }, [currentSessionId]);

  // Start a single-photo scan. If we already know the user's age + sensitivities,
  // thread them straight into the analysis and skip the intake modal — no point
  // re-asking for data we have. Otherwise hold the photo and ask the two questions.
  const beginScan = async (file, angles) => {
    setLoadingPurpose(IMAGE_ROLES.FULL_SKIN_SCAN);
    setScanHandoff(true);
    try {
      // Resolve at scan time rather than trusting preloaded state — the profile fetch
      // may not have landed yet, and a null there re-asked users who already answered.
      const known = resolveScanIntake ? await resolveScanIntake() : scanIntakeKnown;
      if (known) {
        if (angles) await (uploadCaptureImage || uploadImage)(file, angles, known);
        else await uploadImage(file, false, known, {
          ...createImageJourney(IMAGE_ROLES.FULL_SKIN_SCAN),
          analysisType: 'skin',
          freshAnalysis: true,
        });
        return;
      }
      setScanHandoff(false);
      setPendingScan({ file, angles });
    } finally {
      setScanHandoff(false);
    }
  };

  // Once the upload has reached the normal request phase, the parent's loading
  // state takes over with "Analysing your full skin scan…". Until then the
  // handoff remains visible while files are resized, uploaded and signed.
  useEffect(() => {
    if (scanHandoff && isLoading) setScanHandoff(false);
  }, [scanHandoff, isLoading]);

  const submitPendingScan = async (intake) => {
    const scan = pendingScan;
    if (!scan) return;
    setLoadingPurpose(IMAGE_ROLES.FULL_SKIN_SCAN);
    setScanHandoff(true);
    setPendingScan(null);
    try {
      if (intake) onIntakeCollected?.(intake);
      if (scan.angles) {
        await (uploadCaptureImage || uploadImage)(scan.file, scan.angles, intake || undefined);
      } else {
        await uploadImage(scan.file, false, intake || null, {
          ...createImageJourney(IMAGE_ROLES.FULL_SKIN_SCAN),
          analysisType: 'skin',
          freshAnalysis: true,
        });
      }
    } finally {
      setScanHandoff(false);
    }
  };

  // Attach-then-type helpers. Stage a product photo as a composer chip instead of
  // firing analysis immediately; the send handler (submitComposer) dispatches it
  // with whatever the user typed as the prompt. Cleanup revokes the preview blob.
  const stageAttachment = (file, mode = 'product') => setPendingAttachment(prev => {
    // Adding a second PRODUCT photo while one is staged = a multi-product attempt
    // (one product at a time in chat) — log the intent so we can decide whether to
    // route these to Compare later. The cleanup effect revokes the previous preview.
    if (prev && mode === 'product') { try { trackMultiProductAttempt({ count: 2, via: 'add_second' }); } catch { /* analytics best-effort */ } }
    return { file, previewUrl: URL.createObjectURL(file), mode };
  });
  const clearAttachment = () => setPendingAttachment(null);
  useEffect(() => {
    return () => { if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl); };
  }, [pendingAttachment]);

  // Send a staged photo. Routes by the mode captured at attach time. The upload
  // functions capture the current `input` as the prompt via closure, so we clear
  // the box immediately (stops the text sitting visible for the ~18s analysis and
  // getting re-sent as a plain, image-less "which product?" message).
  const submitComposer = () => {
    if (scanHandoff || isLoading) return;
    if (pendingAttachment) {
      const file = pendingAttachment.file;
      const mode = pendingAttachment.mode || 'product';
      const hasPrompt = !!(input && input.trim());
      clearAttachment();
      if (mode === 'photo') {
        setLoadingPurpose('chat');
        // Generic upload: nothing was classified, so let the backend decide.
        // uploadPhoto always sends a prompt (the user's, or a neutral default)
        // which is what makes flexible routing fire instead of the legacy path.
        (uploadPhoto || uploadImage)(file);
      } else if (mode === 'product') {
        setLoadingPurpose('product_reference');
        (uploadProductImage || uploadImage)(file);
      } else if (hasPrompt) {
        setLoadingPurpose('full_skin_scan');
        // Face + a question → flex: the model answers the question AND returns the
        // skin read/update. uploadImage carries the intake + the typed prompt.
        uploadImage(file, false, scanIntakeKnown);
      } else {
        setLoadingPurpose('full_skin_scan');
        // Face, no question → the guided skin read (intake) as before.
        beginScan(file, null);
      }
      setInput('');
      return;
    }
    setLoadingPurpose('chat');
    sendMessage();
  };

  // Warm the FaceLandmarker WASM + model while the selfie guidance modal is up,
  // so the "Preparing camera…" wait is usually gone by the time they tap.
  useEffect(() => {
    if (showImageGuidance && guidanceMode === 'face' && isGuidedCaptureEnabled()) {
      import('./skin-capture/web/faceLandmarker')
        .then((m) => m.preloadFaceLandmarker())
        .catch(() => {});
    }
  }, [showImageGuidance, guidanceMode]);

  useEffect(() => {
    if (autoTriggerUpload) {
      guidanceOpenedAt.current = Date.now();
      setGuidanceStep('choose');
      setShowImageGuidance(true);
      onAutoTriggerUploadHandled?.();
    }
  }, [autoTriggerUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the menu at a specific section on request (used by the post-registration
  // welcome, which lives in ChatComponent and so can't reach this state directly).
  useEffect(() => {
    if (!openMenuAt) return;
    setMenuInitialView(openMenuAt);
    setIsMenuOpen(true);
    onOpenMenuAtHandled?.();
  }, [openMenuAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a skin-analysis-from-profile completes (isLoading goes true→false),
  // flip the context banner from "in progress" to "result ready → view profile".
  // Only show the banner if the last assistant message actually contains a
  // skinAnalysis payload — this prevents false positives from cancelled uploads,
  // product photos, or plain text replies.
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    prevIsLoadingRef.current = isLoading;
    if (wasLoading && !isLoading && skinAnalysisFromProfile) {
      setSkinAnalysisFromProfile(false);
      const lastMsg = messages[messages.length - 1];
      const hasSkinResult = lastMsg?.sender === 'assistant' && lastMsg?.skinAnalysis;
      if (hasSkinResult) {
        setShowProfileResultBanner(true);
      }
      // If no skin result (cancelled / product / error), just clear the flag silently.
    }
  }, [isLoading, skinAnalysisFromProfile, messages]);

  const handleUploadClick = () => {
    // Opening the ordinary chat uploader starts a fresh, flexible attachment.
    // A cancelled outcome picker must not turn this upload into shade evidence
    // or a skin scan later.
    outcomeUploadRef.current = null;
    pendingUploadIntentRef.current = 'photo';
    clearPendingShadePhoto();
    guidanceOpenedAt.current = Date.now();
    setGuidanceStep('choose');
    setShowImageGuidance(true);
  };

  // Outcome onboarding already knows why the person is adding an image. Keep
  // that purpose attached through the native picker and the consent handoff so
  // the backend never has to guess whether a face is a full scan, one area, or
  // colour evidence for shade matching.
  const beginOutcomeUpload = async ({ mode, journey, source = 'skin', cameraFacing = 'user', guided = false }) => {
    if (!imageConsent && requestConsent) {
      const granted = await requestConsent();
      if (!granted) return;
    }
    setLoadingPurpose(
      journey?.referenceKind === 'inspiration'
        ? 'shade_inspiration_personalise'
        : (journey?.imageRole || source)
    );
    if (guided && mode === 'camera' && isGuidedCaptureEnabled()) {
      setShowGuidedCapture(true);
      return;
    }
    outcomeUploadRef.current = { journey, source };
    uploadSourceRef.current = mode;
    if (mode === 'camera') {
      setGuidanceMode(cameraFacing === 'environment' ? 'product' : 'face');
      window.requestAnimationFrame(() => cameraInputRef.current?.click());
    }
    else fileInputRef.current?.click();
  };

  const clearPendingShadePhoto = () => {
    setPendingShadePhoto((pending) => {
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      return null;
    });
  };

  const usePendingShadePhoto = () => {
    if (!pendingShadePhoto) return;
    const { file, journey, source, mode } = pendingShadePhoto;
    trackPhotoUpload({ source: mode, kind: source });
    uploadImage(file, false, null, journey);
    clearPendingShadePhoto();
  };

  useEffect(() => () => {
    if (pendingShadePhoto?.previewUrl) URL.revokeObjectURL(pendingShadePhoto.previewUrl);
  }, [pendingShadePhoto]);

  // mode: 'camera' opens the device camera directly (front for selfie, back
  // for product). 'library' opens the file picker. iOS shows a native sheet
  // that includes both options even from the file input — but Android does
  // not, so we explicitly offer both buttons. Same code path works on iOS,
  // it just defaults to the right action without the user having to drill
  // into a sheet.
  const handleProceedToUpload = async (mode = 'library', intent) => {
    // `intent` overrides guidanceMode for same-tick calls (state isn't applied yet).
    const effectiveIntent = intent || guidanceMode;
    outcomeUploadRef.current = null;
    pendingUploadIntentRef.current = effectiveIntent;
    setShowImageGuidance(false);
    // Gate consent BEFORE opening the file picker — this was the original design
    // and matches the privacy flow rules (ask first, then pick file). If the
    // browser blocks the picker due to async context, user can tap Upload again.
    if (!imageConsent && requestConsent) {
      const granted = await requestConsent();
      if (!granted) return;
    }
    // Guided skin-capture: intercept the selfie camera path only. Library picks
    // and product photos fall through to the existing native inputs untouched.
    if (mode === 'camera' && effectiveIntent === 'face' && isGuidedCaptureEnabled()) {
      setShowGuidedCapture(true);
      return;
    }
    if (mode === 'camera') {
      uploadSourceRef.current = 'camera';
      cameraInputRef.current?.click();
    } else {
      uploadSourceRef.current = 'library';
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const outcomeUpload = outcomeUploadRef.current;
      outcomeUploadRef.current = null;
      if (outcomeUpload) {
        if (outcomeUpload.journey?.imageRole === IMAGE_ROLES.FULL_SKIN_SCAN) {
          trackPhotoUpload({ source: uploadSourceRef.current, kind: outcomeUpload.source });
          beginScan(files[0], null);
          e.target.value = '';
          return;
        }
        if (outcomeUpload.journey?.imageRole === IMAGE_ROLES.SHADE_SKIN_REFERENCE) {
          setPendingShadePhoto({
            file: files[0],
            journey: outcomeUpload.journey,
            source: outcomeUpload.source,
            mode: uploadSourceRef.current,
            previewUrl: URL.createObjectURL(files[0]),
          });
          e.target.value = '';
          return;
        }
        trackPhotoUpload({ source: uploadSourceRef.current, kind: outcomeUpload.source });
        uploadImage(files[0], false, null, outcomeUpload.journey);
        e.target.value = '';
        return;
      }
      const uploadIntent = pendingUploadIntentRef.current || 'photo';
      pendingUploadIntentRef.current = 'photo';
      // Where this photo came from — camera vs library, set in handleProceedToUpload.
      trackPhotoUpload({ source: uploadSourceRef.current, kind: uploadIntent === 'face' ? 'skin' : uploadIntent });
      // Single photo: a PRODUCT photo is staged in the composer (attach-then-type)
      // so the user can add a question before sending; a FACE photo goes straight
      // into the guided skin scan + intake as before. Multi-image keeps the batch
      // skin path.
      if (files.length === 1) {
        // Both face AND product photos are STAGED now, so the user can add a
        // question. Send routes by mode (submitComposer): product → product read;
        // face + question → flex skin read that also answers; face + no question →
        // the guided skin scan as before.
        stageAttachment(files[0], uploadIntent);
      } else if (uploadIntent !== 'face') {
        // Multi-select of products: log intent (Compare demand) + stage the first.
        trackMultiProductAttempt({ count: files.length, via: 'multi_select' });
        stageAttachment(files[0], 'product');
      } else {
        uploadImages(Array.from(files), '');
      }
      e.target.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      trackPhotoUpload({ source: 'drop', kind: 'photo' });
      // A dropped image has the same flexible meaning as a normal chat
      // attachment, including the chance to type a question before sending.
      if (files.length === 1) {
        stageAttachment(files[0], 'photo');
      } else {
        uploadImages(files, '');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitComposer();
    // Input is cleared by sendMessage (text) or by the upload (staged photo).
    // Reset the textarea height after send.
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = '20px'; // Force to single line height
      }
    }, 0);
  };

  useEffect(() => {
  // 2026-06-09 (scroll-lag #5): the previous version fired scrollIntoView
    // THREE times per message change (50/200/500ms timeouts). Streaming a
    // long reply triggers messages updates every ~30ms, so the page was
    // calling smooth-scroll dozens of times per second — main thread thrash
    // = the "stuck/sometimes-doesn't-scroll" feeling. Replaced with a single
    // rAF + one short fallback for the layout-stabilises-late case.
    if (!messagesContainerRef.current || isLandingState || !isNearBottomRef.current) return;
    const scrollToBottom = () => {
      scrollChatToBottom(messagesContainerRef.current);
    };
    const rafId = requestAnimationFrame(scrollToBottom);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [messages, isLandingState]);

  // Scroll when loading state changes — both directions so the loading indicator is always visible
  useEffect(() => {
    if (messagesContainerRef.current && !isLandingState && isNearBottomRef.current) {
      scrollChatToBottom(messagesContainerRef.current);
    }
  }, [isLoading, isLandingState]);

  // Keep the chooser at the top; scroll chat conversations to the newest turn.
  // 2026-06-09 (scroll-lag #5): same triple-timeout → rAF + single fallback.
  useEffect(() => {
    if (isLandingState) {
      if (messagesContainerRef.current) messagesContainerRef.current.scrollTop = 0;
      return;
    }
    const scrollToBottom = () => {
      scrollChatToBottom(messagesContainerRef.current);
    };

    const rafId = requestAnimationFrame(scrollToBottom);
    const timeoutId1 = setTimeout(scrollToBottom, 300);  // single fallback

    // Also handle visual viewport changes (iOS keyboard show/hide).
    if (window.visualViewport) {
      const handleViewportChange = () => {
        setTimeout(scrollToBottom, 100);
      };
      window.visualViewport.addEventListener('resize', handleViewportChange);

      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId1);
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      };
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId1);
    };
  }, [isLandingState]);

  // Helper function to detect if message contains product recommendations
  const hasProductRecommendations = (message) => {
    if (message.sender !== 'assistant') return false;
    if (message.skipFeedback) return false;

    // If backend flagged that search was used, treat as recommendation message
    if (message.searchUsed) return true;

    // Only analyze plain string messages
    const text = typeof message.text === 'string' ? message.text.toLowerCase() : '';
    if (!text) return false;

    const productKeywords = [
      'foundation', 'concealer', 'lipstick', 'mascara', 'eyeshadow', 'blush',
      'serum', 'moisturizer', 'cleanser', 'toner', 'sunscreen', 'retinol',
      'price', '$', 'buy', 'purchase', 'available', 'sephora', 'ulta', 'amazon',
      'brand', 'product', 'recommend', 'suggestion'
    ];

    return productKeywords.some((keyword) => text.includes(keyword)) &&
      (text.includes('recommend') || text.includes('suggest') || text.includes('try'));
  };

  const handleNavigate = (section) => {
    setCurrentView(section);
    setIsMenuOpen(false);
  };

  const handleBackToChat = () => {
    setCurrentView('chat');
  };

  // Render different views based on currentView
  if (currentView === 'progress') {
    return <ProgressTrackerPlaceholder onBack={handleBackToChat} />;
  }

  return (
    <div 
      className={`flex flex-col overflow-hidden chat-container ${isDragOver ? 'bg-[#F3D5B5]/20' : ''} ${isDesktop ? 'chat-layout-desktop' : ''}`}
      style={{ background: '#E5DDD4' }}
      data-desktop={isDesktop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Wrapper: full width on desktop so header is edge-to-edge */}
      <div
        className="chat-outer-wrapper w-full flex flex-col mx-auto flex-1 min-h-0"
        style={{
          background: '#FFFFFF',
          boxShadow: '0 0 60px rgba(0,0,0,0.15)',
          maxWidth: isDesktop ? '100%' : undefined,
        }}
      >
      {/* Header: full width on all viewports */}
      <header className="chat-header bg-[#341C1C] text-white px-5 sm:px-8 py-3.5 sm:py-4 flex items-center sticky top-0 z-10 w-full min-w-0">
        {/* Left: Chats icon */}
        <button
          onClick={() => setIsHistoryOpen(true)}
          className="text-white/70 hover:text-white transition-colors flex-shrink-0 w-10 flex items-center justify-start"
          title="Chat history"
          aria-label="Open chat history"
        >
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
          </svg>
        </button>

        {/* Center: Son:mi branding */}
        <div className="chat-header-brand absolute left-1/2 -translate-x-1/2 flex flex-col items-center min-w-0">
          <div className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#F3D5B5" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C12 2 13 9 14.5 10.5C16 12 22 12 22 12C22 12 16 12 14.5 13.5C13 15 12 22 12 22C12 22 11 15 9.5 13.5C8 12 2 12 2 12C2 12 8 12 9.5 10.5C11 9 12 2 12 2Z"/>
            </svg>
            <h1 className="text-base font-semibold text-white tracking-wide">Son:mi</h1>
          </div>
          {uploadedImages && uploadedImages.length > 0 && showAnalyzedPhotos && (
            <div className="chat-header-status text-[13px] text-white/50 tracking-wide mt-0.5">
              {uploadedImages.length} photo{uploadedImages.length > 1 ? 's' : ''} analysed
              {analysisProgress > 0 && ` · ${Math.round(analysisProgress)}%`}
            </div>
          )}
        </div>

        <div className="chat-header-actions ml-auto flex items-center gap-2 flex-shrink-0">
        {isAnonymous && onPersistentSignIn && (
          <button
            type="button"
            onClick={onPersistentSignIn}
            className="chat-header-sign-in rounded-full border border-white/25 px-3 py-1.5 text-[13px] font-semibold text-[#F3D5B5] hover:border-white/50 hover:text-white transition-colors"
          >
            Sign in
          </button>
        )}
        {/* Right: Menu icon */}
        <button
          onClick={async () => {
            trackMenuOpened();
            setIsMenuOpen(true);
            // Skip refetch within TTL — same rationale as onLoadProducts below.
            if (!userId) return;
            const sinceLastFetch = Date.now() - lastSavedProductsFetchRef.current;
            if (sinceLastFetch < SAVED_PRODUCTS_TTL_MS) return;
            const products = await getSavedProductsFromBackend(userId);
            if (products) {
              const refreshed = await refreshProductImages(products);
              setSavedProducts(refreshed);
              lastSavedProductsFetchRef.current = Date.now();
            }
          }}
          className="text-white/70 hover:text-white transition-colors flex-shrink-0 w-10 flex items-center justify-end"
        >
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        </div>
      </header>
      
      {/* Uploaded Images Preview */}
      {uploadedImages && uploadedImages.length > 0 && showAnalyzedPhotos && (
        <div className="bg-white/50 px-4 py-2 border-b border-white/20">
          <div className="flex items-center space-x-2 overflow-x-auto">
            <span className="text-xs text-[#341C1C] font-medium whitespace-nowrap">
              Analysed Photos:
            </span>
            {uploadedImages.map((image, index) => (
              <div key={index} className="flex-shrink-0 relative">
                <img 
                  src={image.url} 
                  alt={`Analysis ${index + 1}`}
                  className="w-8 h-8 rounded-full object-cover border-2 border-[#F3D5B5]"
                />
                <div className="absolute -top-1 -right-1 bg-[#F3D5B5] text-[#341C1C] text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Profile analysis in-progress banner */}
      {skinAnalysisFromProfile && !showProfileResultBanner && (
        <div style={{
          background: 'linear-gradient(135deg, #341C1C, #5a3030)',
          color: 'white',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          fontWeight: 500,
        }}>
          <svg style={{ width: 16, height: 16, flexShrink: 0 }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
          <span style={{ flex: 1 }}>Analysing your skin. Results will appear below…</span>
        </div>
      )}

      {/* Profile analysis complete banner */}
      {showProfileResultBanner && (
        <div style={{
          background: 'linear-gradient(135deg, #2E7D32, #388E3C)',
          color: 'white',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          fontWeight: 500,
        }}>
          <svg style={{ width: 16, height: 16, flexShrink: 0 }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span style={{ flex: 1 }}>Analysis complete. Scroll up to see your results</span>
          <button
            onClick={() => {
              setMenuInitialView('personal-profile');
              setIsMenuOpen(true);
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              borderRadius: 20,
              padding: '5px 12px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            View Profile →
          </button>
          <button
            onClick={() => setShowProfileResultBanner(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '0 4px',
              flexShrink: 0,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Messages: on desktop, centered column with extra padding above input */}
      <div
        ref={messagesContainerRef}
        data-landing={isLandingState ? 'true' : undefined}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 space-y-2 sm:space-y-3 min-w-0 chat-desktop-content chat-messages-area"
        style={{
          paddingTop: 20,
          // height + padding-bottom now come from flex (.chat-messages-area is
          // flex-1 min-h-0) and CSS. The old fixed height/minHeight fought the
          // flex column and referenced the retired --vh keyboard math.
          ...(isDesktop ? { maxWidth: window.innerWidth >= 1024 ? 800 : 720, marginLeft: 'auto', marginRight: 'auto', width: '100%' } : {}),
        }}
      >
        {/* 2026-07 onboarding revamp: a brand-new chat shows the landing hero
            (ported from native) instead of the welcome text bubble — the scan
            is the hook, so it gets the whole screen and one obvious CTA. */}
        {isLandingState && (showOutcomeLanding ? (
          <OutcomeLanding
            key={currentSessionId || 'new-conversation'}
            initialScreen={new URLSearchParams(window.location.search).get('journey') === 'colour-makeup' ? 'colour-makeup' : 'home'}
            onFullSkin={({ mode }) => {
              trackOutcomeJourneyStarted('full_skin_scan');
              beginOutcomeUpload({
                mode,
                source: 'full_skin_scan',
                guided: true,
                journey: {
                  ...createImageJourney(IMAGE_ROLES.FULL_SKIN_SCAN),
                  analysisType: 'skin',
                  freshAnalysis: true,
                  promptOverride: 'Run a complete skin analysis from this whole-face photo.',
                },
              });
            }}
            onFocusedSkin={({ mode, area }) => {
              trackOutcomeJourneyStarted(`focused_skin_area:${area}`);
              beginOutcomeUpload({
                mode,
                source: 'focused_skin_area',
                journey: {
                  ...createImageJourney(IMAGE_ROLES.FOCUSED_SKIN_AREA, { analysisScope: area }),
                  analysisType: 'auto',
                  promptOverride: `Analyse only the ${area} visible in this photo. Describe only what is visible in that area, explain the limits of the image, and do not infer my overall skin type, full-face condition, or shade profile.`,
                },
              });
            }}
            onShadeProduct={({ mode, product, catalogProductId }) => {
              if (!product) return;
              trackOutcomeJourneyStarted('shade_specific_product');
              beginOutcomeUpload({
                mode,
                source: 'shade_skin_reference',
                journey: {
                  ...createImageJourney(IMAGE_ROLES.SHADE_SKIN_REFERENCE),
                  analysisType: 'auto',
                  productTitle: product,
                  catalogProductId,
                  promptOverride: `Find my closest shade in ${product}. Use this image only to estimate visible depth, undertone and colour values for shade matching. Do not create a skin or face analysis. If the product line is ambiguous, ask me to choose the exact range.`,
                },
              });
            }}
            onShadeProductReference={({ mode, product }) => {
              trackOutcomeJourneyStarted('shade_product_reference');
              beginOutcomeUpload({
                mode,
                source: 'product_reference',
                cameraFacing: 'environment',
                journey: {
                  ...createImageJourney(IMAGE_ROLES.PRODUCT_REFERENCE),
                  analysisType: 'auto',
                  searchedProduct: product || '',
                  promptOverride: `Identify the exact brand and complexion-product range shown in this label or screenshot${product ? `; I searched for ${product}` : ''}. This image is product evidence for a shade match. Do not analyse a face or give a full formula review. If the exact range is still unclear, ask one concise clarifying question. Once it is identified, ask me to add my skin photo for the match.`,
                },
              });
            }}
            onShadeEstimate={({ currentProduct, currentShade, targetProduct }) => {
              trackOutcomeJourneyStarted('shade_from_owned');
              setLoadingPurpose('shade_estimate');
              sendMessage({
                displayText: `Match ${currentProduct} ${currentShade} to ${targetProduct}`,
                prompt: `Estimate my shade in ${targetProduct} from ${currentProduct} in ${currentShade}. State that this is an estimate based on ${currentProduct} ${currentShade} and that no skin photo was used. If either product line is ambiguous, ask me to choose the exact range.`,
                journeyAction: 'refine_shade_estimate',
                journeyContext: { targetProduct, currentProduct, currentShade },
              });
            }}
            onShadeLook={({ mode, note }) => {
              trackOutcomeJourneyStarted('shade_inspiration');
              beginOutcomeUpload({
                mode,
                cameraFacing: 'environment',
                source: 'inspiration_reference',
                journey: {
                  ...createImageJourney(IMAGE_ROLES.INSPIRATION_REFERENCE),
                  analysisType: 'auto',
                  promptOverride: `${note ? `What I like or want to recreate: ${note}. ` : ''}Analyse the colours and makeup choices in this inspiration image. It may show somebody else: do not analyse it as my face, do not create a skin analysis, and do not save it as my skin profile. Explain the palette in chat, then tell me I can add my own skin photo if I want to adapt the look to me.`,
                  ...(note ? { displayPrompt: note } : {}),
                },
              });
            }}
            onProductAnalysis={() => { trackOutcomeJourneyStarted('product_analysis'); openProductAnalysis(); }}
            onProductCompare={() => { trackOutcomeJourneyStarted('product_comparison'); openProductComparison(); }}
            onChatNow={() => setDirectChatSessionId(directChatKey)}
            onOutcomeSelected={trackOutcomeSelected}
          />
        ) : (
          <LandingHero
            onScan={handleUploadClick}
            prompts={suggestedQuestions && suggestedQuestions.length > 0 ? suggestedQuestions : DEFAULT_EMPTY_SUGGESTED_QUESTIONS}
            onPick={onSuggestedQuestionClick}
          />
        ))}

        {/* OPTIMIZATION: Only render last 50 messages for better performance */}
        {!isLandingState && messages.slice(-50).map((msg, i) => {
          const visibleStart = Math.max(0, messages.length - 50);
          const messageRowKey = `${msg.timestamp || 'no-ts'}-${visibleStart + i}-${msg.sender}`;
          // A reply already sent after this assistant turn replaces its
          // tappable clarification choices, including after chat reload.
          const clarifyAnswered = messages[visibleStart + i + 1]?.sender === 'user';
          const isOnlyWelcomeMessage = messages.length === 1 && msg.sender === 'assistant';
          // Skip empty user bubbles on reload. The guided scan persists a canned
          // "I've uploaded an image…" user message that strips to nothing and
          // carries no photo (imageUrls: []); without this it renders as an empty
          // shell above the rehydrated scan card. (Live, the scan shows no user
          // bubble at all.) Only suppresses user rows with no text AND no image.
          if (msg.sender === 'user') {
            const _stripped = typeof msg.text === 'string' ? stripImageBoilerplate(msg.text) : msg.text;
            const _hasImg = !!(msg.imageUrl || msg.images?.length || msg.imageUrls?.length);
            if (!_stripped && !_hasImg) return null;
          }
          return (
          <div key={messageRowKey} className="w-full min-w-0">
            <div
              className={`chat-bubble ${msg.sender === 'user' ? 'chat-bubble--user' : 'chat-bubble--assistant'} ${isOnlyWelcomeMessage ? 'chat-bubble--welcome' : ''} ${msg.isFollowUp ? 'chat-bubble--followup' : ''} ${
                msg.sender === 'user'
                  ? ''
                  : msg.isFollowUp
                    ? 'text-[#2A1212] mr-auto w-full max-w-full pl-4 pr-1 py-2 border-l-2 border-[#E8C99A] italic'
                    : 'text-[#2A324B] mr-auto w-full max-w-full px-1 py-2'
              }`}
              style={{
                wordWrap: 'break-word',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                minWidth: '0',
                ...(msg.isFollowUp ? { fontSize: '16px' } : {})
              }}
            >
              {msg.sender === 'assistant' ? (
                msg.isLoading ? (
                  <div className="flex items-center gap-2 text-[#2A324B]/70">
                    <svg className="animate-spin h-4 w-4 text-[#341C1C] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">{msg.text || 'Analysing…'}</span>
                  </div>
                ) : typeof msg.text === 'object' && msg.text?.type === 'service_unavailable' ? (
                  <ServiceUnavailableMessage isAnonymous={isAnonymous} userId={userId} />
                ) : typeof msg.text === 'object' && msg.text.type === 'product_analysis' ? (
                  <ProductAnalysisCard
                    analysis={msg.text.data}
                    productName={msg.text.productName}
                    productBrand={msg.text.productBrand}
                    productImage={msg.text.productImage}
                    onSave={saveProductFromRecommendations}
                    customListNames={customListNames}
                    onSeeFullAnalysis={openProductAnalysis}
                  />
                ) : typeof msg.text === 'object' &&
                     (msg.text.type === 'product_recommendations' || (msg.text.products && msg.text.advice)) && 
                     ((msg.text.data && msg.text.data.product_matches) || msg.text.product_matches) ? (
                  <ProductRecommendations
                    recommendations={msg.text.data || msg.text}
                    onSave={saveProductFromRecommendations}
                    customListNames={customListNames}
                    createCustomList={createCustomList}
                  />
                ) : msg.isStructured ? (
                  (() => {
                    // Which section (if any) this turn should invite them into.
                    //   · one skincare product -> the full formula breakdown
                    //   · routine / several products -> My Routine (or Scan, when
                    //     they have nothing saved yet)
                    //   · makeup or shade matching -> NO CTA: Product Analysis is
                    //     skincare-shaped and pointing there would be a downgrade
                    // Both CTAs ride at the TOP of their tab (Sofia 2026-08-26):
                    // routine CTA -> top of the Routine tab, product CTA -> top of
                    // the Advice tab. StructuredMessage places it from cta.placement
                    // and the model's advice flows underneath. Makeup / shade turns
                    // still get no CTA (ctaForTurn returns null).
                    const cta = ctaForTurn(msg);
                    return (
                      <>
                        <StructuredMessage
                          content={normalizeStructuredContent(msg.text)}
                          onSaveProduct={saveProductFromRecommendations}
                          userId={userId}
                          shadeMatches={msg.shadeMatches || (typeof msg.text === 'object' ? msg.text?.shadeMatches : undefined)}
                          onAsk={(text) => onSuggestedQuestionClick?.(text, 'inline_follow_up') || (!onSuggestedQuestionClick && sendMessage(text))}
                          clarifyAnswered={clarifyAnswered}
                          sectionCta={cta}
                        />
                      </>
                    );
                  })()
                ) : skinReadMarkdown(msg) ? (
                  <SkinAnalysisCard
                    analysis={skinReadMarkdown(msg)}
                    imageUrl={msg.scanImageUrl || (msg.images && msg.images.length > 0 ? msg.images[0].url : null)}
                    isScan={!!msg.isSkinScan}
                    photoCount={msg.scanPhotoCount || 0}
                    angleImages={msg.scanAngleImages || null}
                    userId={userId}
                    isAnonymous={isAnonymous}
                    onShowSignIn={onShowSignIn}
                    onAddRoutine={() => {
                      // Building/tracking a routine is a logged-in feature.
                      if (isAnonymous) { onShowSignIn?.(); return; }
                      setMenuInitialView('routine');
                      setIsMenuOpen(true);
                    }}
                    onAnalyseProduct={() => {
                      // Opens Product Analysis flow from the menu — same entry
                      // point used by the Take-a-picture CTAs.
                      setMenuInitialView('product-analysis');
                      setIsMenuOpen(true);
                    }}
                    insight={(msg.skinAnalysis && typeof msg.skinAnalysis === 'object' && msg.skinAnalysis.insight) || ''}
                    onReadFeedback={(verdict) => onReadFeedback?.(verdict)}
                    onCorrectionSubmit={(payload) => onCorrectionSubmit?.(payload)}
                    onSeeShadeMatches={() => {
                      onShadeMatchesRequested?.();
                      // 2026-06-04: pass the message explicitly to bypass
                      // the setInput → state propagation → sendMessage race
                      // that was leaving the user with no visible bubble.
                      // sendMessage(text) renders the user-bubble immediately
                      // and submits the same string to the backend, so the
                      // user clearly sees what they "asked".
                      sendMessage("Show me foundation shade matches for my skin tone");
                    }}
                  />
                ) : typeof msg.text === 'object' && msg.text?.type === 'product_followup' ? (
                  <ProductAnalysisSummaryCard data={msg.text} />
                ) : typeof msg.text === 'object' && msg.text !== null &&
                    ['advice', 'products', 'routine', 'general'].some((k) => Object.prototype.hasOwnProperty.call(msg.text, k)) ? (
                  <StructuredMessage
                    content={normalizeStructuredContent(msg.text)}
                    onSaveProduct={saveProductFromRecommendations}
                    userId={userId}
                    shadeMatches={msg.shadeMatches || (typeof msg.text === 'object' ? msg.text?.shadeMatches : undefined)}
                    onAsk={(text) => onSuggestedQuestionClick?.(text, 'inline_follow_up') || (!onSuggestedQuestionClick && sendMessage(text))}
                    clarifyAnswered={clarifyAnswered}
                  />
                ) : typeof msg.text === 'string' ? (
                  <StructuredMessage
                    content={normalizeStructuredContent({
                      advice: stripLeadingToolCallJson(msg.text),
                      products: null,
                      routine: null,
                    })}
                    onSaveProduct={saveProductFromRecommendations}
                    userId={userId}
                    shadeMatches={msg.shadeMatches || (typeof msg.text === 'object' ? msg.text?.shadeMatches : undefined)}
                    onAsk={(text) => onSuggestedQuestionClick?.(text, 'inline_follow_up') || (!onSuggestedQuestionClick && sendMessage(text))}
                    clarifyAnswered={clarifyAnswered}
                    noReasoningSplit={!!msg.isWelcome}
                  />
                ) : (
                  <div className="text-sm text-[#2A324B]/80">{typeof msg.text === 'string' ? msg.text : 'Message could not be displayed.'}</div>
                )
              ) : (
                (() => {
                  const display = stripImageBoilerplate(msg.text);
                  return display ? <div>{display}</div> : null;
                })()
              )}
              {msg.sender === 'assistant' && journeyActionFor(msg) === 'personalise_inspiration' && (
                <InspirationPersonaliseCta
                  onClick={() => {
                    trackJourneyNextActionSelected('adapt_look', 'shade_inspiration');
                    trackOutcomeJourneyStarted('shade_inspiration_personalise');
                    const journeyContext = journeyContextFor(msg);
                    beginOutcomeUpload({
                      mode: 'library',
                      source: 'shade_inspiration_personalise',
                      journey: {
                        ...createImageJourney(IMAGE_ROLES.SHADE_SKIN_REFERENCE),
                        analysisType: 'auto',
                        referenceImageUrl: journeyContext.referenceImageUrl || '',
                        referenceKind: 'inspiration',
                        promptOverride: 'Using my skin photo and the colour analysis from the previous inspiration image, adapt that look to my visible depth and undertone. Use this image only for colour matching. Do not create a skin or face analysis and do not save it as my skin profile.',
                      },
                    });
                  }}
                  secondaryActions={[
                    {
                      label: 'Find products in these colours',
                      onClick: () => {
                        trackJourneyNextActionSelected('find_products', 'shade_inspiration');
                        sendMessage('Find products in these colours that recreate the look.');
                      },
                    },
                    {
                      label: 'Turn this into steps',
                      onClick: () => {
                        trackJourneyNextActionSelected('make_steps', 'shade_inspiration');
                        sendMessage('Turn this look into clear application steps.');
                      },
                    },
                  ]}
                />
              )}
              {msg.sender === 'assistant' && journeyActionFor(msg) === 'add_skin_for_product_reference' && (
                <InspirationPersonaliseCta
                  title="Add my skin for this match"
                  detail="Use one skin photo to match within this range"
                  onClick={() => {
                    trackJourneyNextActionSelected('add_skin', 'shade_product_reference');
                    trackOutcomeJourneyStarted('shade_product_reference_add_skin');
                    const journeyContext = journeyContextFor(msg);
                    beginOutcomeUpload({
                      mode: 'library',
                      source: 'shade_skin_reference',
                      journey: {
                        ...createImageJourney(IMAGE_ROLES.SHADE_SKIN_REFERENCE),
                        analysisType: 'auto',
                        productTitle: journeyContext.productTitle || '',
                        catalogProductId: journeyContext.catalogProductId || '',
                        referenceImageUrl: journeyContext.referenceImageUrl || '',
                        promptOverride: 'Match my visible depth and undertone to the product range identified from the previous reference. Do not create a skin or face analysis.',
                      },
                    });
                  }}
                />
              )}
              {msg.sender === 'assistant' && journeyActionFor(msg) === 'refine_shade_estimate' && (
                <InspirationPersonaliseCta
                  title="Add a skin photo to refine"
                  detail="Use one skin photo to verify the estimate"
                  onClick={() => {
                    const journeyContext = journeyContextFor(msg);
                    trackJourneyNextActionSelected('refine_with_skin', 'shade_from_owned');
                    trackOutcomeJourneyStarted('shade_from_owned_refine');
                    beginOutcomeUpload({
                      mode: 'library',
                      source: 'shade_from_owned_refine',
                      journey: {
                        ...createImageJourney(IMAGE_ROLES.SHADE_SKIN_REFERENCE),
                        analysisType: 'auto',
                        productTitle: journeyContext.targetProduct || '',
                        promptOverride: `Refine the previous cross-brand shade estimate in ${journeyContext.targetProduct || 'the target product'} using only my visible depth and undertone. Match within that product range. If the exact range is still ambiguous, ask one concise clarification. Do not create a skin or face analysis and do not save this image as my skin profile.`,
                      },
                    });
                  }}
                />
              )}
              {msg.imageUrl && (
                <div>
                  <img
                    src={msg.imageUrl}
                    alt="Uploaded"
                    style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '8px' }}
                    onError={(e) => {
                      // Replace image with fallback text
                      e.target.style.display = 'none';
                      const fallbackDiv = document.createElement('div');
                      fallbackDiv.style.cssText = 'max-width: 200px; border-radius: 8px; margin-top: 8px; padding: 12px; background: #f3f4f6; border: 1px dashed #d1d5db; color: #6b7280; font-size: 14px; text-align: center;';
                      fallbackDiv.textContent = '📷 Image analysed';
                      e.target.parentNode.insertBefore(fallbackDiv, e.target.nextSibling);
                    }}
                    onLoad={() => {
                    }}
                  />
                  {/* Show save buttons only for product images */}
                  {msg.imageType === 'product' && (
                    <ProductSaveButtons
                      productImage={msg.imageUrl}
                      productName={msg.productName || 'Unknown Product'}
                      productBrand={msg.productBrand || 'Unknown Brand'}
                      onSave={saveProduct}
                      customListNames={customListNames}
                      createCustomList={createCustomList}
                    />
                  )}
                </div>
              )}
              
              {/* Display multiple images if present (from analysis or from database).
                  Skip for skin-analysis messages: the photo is already the card hero
                  (scanImageUrl), so this generic "N image analysed" thumbnail is a
                  redundant duplicate that ONLY appears on reload — a live scan carries
                  no imageUrls, so nothing renders here. Suppressing it keeps history
                  identical to the just-after-analysis view (Sofia). */}
              {!skinReadMarkdown(msg) && (msg.images || msg.imageUrls) && (msg.images?.length > 0 || msg.imageUrls?.length > 0) && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    {(msg.images || msg.imageUrls || []).map((image, index) => {
                      const imageUrl = typeof image === 'string' ? image : image.url;
                      return (
                      <div key={index} className="relative">
                        <img
                          src={imageUrl}
                          alt={`Analysis ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border-2 border-[#F3D5B5]"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.className = 'w-24 h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-500 text-xs';
                            fallbackDiv.textContent = '📷';
                            e.target.parentNode.insertBefore(fallbackDiv, e.target.nextSibling);
                          }}
                          onLoad={() => {
                          }}
                        />
                        <div className="absolute -top-1 -right-1 bg-[#F3D5B5] text-[#2A324B] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                  <div className="text-xs text-[#2A324B]/70 mt-2">
                    {(msg.images || msg.imageUrls || []).length} image{(msg.images || msg.imageUrls || []).length > 1 ? 's' : ''} analysed
                  </div>
                </div>
              )}
            </div>
            
            {/* Show feedback UI for product recommendation messages.
                2026-06-04: hidden for the TikTok demo — the "Was this helpful?" pill
                reads as friction during a live demo and we need to redesign the
                feedback capture properly before reintroducing it. Toggle by setting
                REACT_APP_SHOW_PRODUCT_FEEDBACK=true. */}
            {hasProductRecommendations(msg) && process.env.REACT_APP_SHOW_PRODUCT_FEEDBACK === 'true' && (
              <div className="max-w-sm mr-auto">
                <ProductFeedback
                  messageIndex={i}
                  onRefineResults={onRefineResults}
                />
              </div>
            )}
            
            {/* Show upgrade prompt when search limit is reached */}
            {msg.showUpgradePrompt && (
              <div className="max-w-md mr-auto mt-2">
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-800 text-sm">Ready for unlimited searches?</h4>
                      <p className="text-purple-600 text-xs mt-1">Get instant access to the latest products & prices</p>
                    </div>
                    <button
                      onClick={() => onRequestUpgrade && onRequestUpgrade()}
                      className="ml-3 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-sm"
                    >
                      Request Access
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );})}
        
        {/* Loading indicator when waiting for response */}
        {(isLoading || scanHandoff) && (
          <div className="w-full min-w-0">
            <div className="chat-bubble chat-bubble--assistant text-[#2A324B] mr-auto w-full max-w-full px-1 py-2">
              <div className="flex items-center gap-2 text-[#2A324B]/70" style={{ fontSize: '14px' }}>
                <svg className="animate-spin h-4 w-4 text-[#341C1C] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">{scanHandoff && !isLoading ? 'Preparing your skin scan…' : (streamStatus || (loadingPurpose === 'shade_inspiration_personalise' ? 'Adapting the look to your colouring…' : loadingPurpose === IMAGE_ROLES.SHADE_SKIN_REFERENCE || loadingPurpose === 'shade_estimate' ? 'Finding the closest shade match…' : loadingPurpose === IMAGE_ROLES.INSPIRATION_REFERENCE ? 'Reading the colours and makeup in this look…' : loadingPurpose === IMAGE_ROLES.PRODUCT_REFERENCE ? 'Identifying the product…' : loadingPurpose === IMAGE_ROLES.FOCUSED_SKIN_AREA ? 'Analysing only the area you selected…' : loadingPurpose === IMAGE_ROLES.FULL_SKIN_SCAN ? 'Analysing your full skin scan…' : "This may take a moment. Please don't close the page."))}</span>
              </div>
              <TypingFact isLoading={isLoading} topic={loadingPurpose} />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {/* Scroll to bottom button - floating circle only */}
      {showScrollButton && currentView === 'chat' && !isMenuOpen && !showGuidedCapture && !keyboardOpen && (
        <button
          onClick={() => {
            isNearBottomRef.current = true;
            scrollChatToBottom(messagesContainerRef.current, 'smooth');
          }}
          className="fixed bottom-24 right-6 bg-[#341C1C] hover:bg-[#341C1C]/90 text-white h-12 w-12 rounded-full flex items-center justify-center transition-colors shadow-xl z-50 border-2 border-white"
          title="Scroll to bottom"
          style={{ 
            position: 'fixed',
            zIndex: 9999,
            backgroundColor: '#341C1C',
            borderRadius: '50%'
          }}
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}

      {/* Input Section: on desktop, same centered column and slimmer bar.
          2026-06-04: positioning is handled by .chat-input-area (position: fixed)
          in index.css. The Tailwind `sticky bottom-0` was removed because the
          two position rules conflict and on Android Chrome the `sticky` was
          winning — leaving the input pinned to the parent flex container's
          bottom (off-screen below the keyboard) and exposing the messages'
          padding-bottom as a tall peach gap. */}
      <div
        className={`px-4 sm:px-6 pt-2 pb-3 bg-white flex-shrink-0 chat-input-area chat-desktop-content chat-input-fade ${isLandingState ? 'chat-input-area--landing' : ''}`}
        style={{
          ...(isDesktop ? { maxWidth: window.innerWidth >= 1024 ? 800 : 720, marginLeft: 'auto', marginRight: 'auto', width: '100%', paddingTop: 8, paddingBottom: 12, paddingLeft: 24, paddingRight: 24 } : {}),
        }}
      >
        {!isLandingState && (<>

        {/* Empty chat: Upload Your Selfie as its own block above suggested questions and input */}
        {/* Superseded by LandingHero on a brand-new chat — it carries the scan
            CTA and the "Not sure what to ask?" prompts itself. Kept for the
            edge case of a single non-welcome assistant message. */}
        {!isLandingState && messages.length === 1 && messages[0]?.sender === 'assistant' && !isLoading && (
          <>
            {!enteredDirectChat && <div className="chat-empty-state-cta">
              <button
                type="button"
                onClick={handleUploadClick}
                className="chat-empty-state-cta-button w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl bg-white hover:bg-[#FAF7F2] text-[#2A1212] transition-colors border border-[#E5E0D8] text-left active:scale-[0.99]"
              >
                <div className="w-10 h-10 rounded-full bg-[#FDF6EE] flex items-center justify-center flex-shrink-0">
                  <svg className="w-[18px] h-[18px] text-[#7A4040]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[15px] text-[#2A1212]">Upload a selfie</div>
                  <div className="text-xs text-[#2A324B]/50 mt-0.5">Get an instant skin analysis</div>
                </div>
              </button>
            </div>}
            {!suggestionsDismissed && (
              <div className="chat-empty-state-questions">
                <div className="mb-2 text-[13px] font-semibold uppercase tracking-[0.16em] text-[#2A324B]/50">Or try asking</div>
                <SuggestedQuestions
                  questions={suggestedQuestions && suggestedQuestions.length > 0 ? suggestedQuestions : DEFAULT_EMPTY_SUGGESTED_QUESTIONS}
                  onQuestionClick={onSuggestedQuestionClick}
                  onDismiss={onDismissSuggestions}
                  isLoading={isLoading}
                />
              </div>
            )}
          </>
        )}

        {/* Suggested Questions when chat has messages */}
        {!suggestionsDismissed && !gateActive && messages.length > 1 && !isLoading && (
          <SuggestedQuestions
            questions={suggestedQuestions && suggestedQuestions.length > 0 ? suggestedQuestions : DEFAULT_EMPTY_SUGGESTED_QUESTIONS}
            onQuestionClick={onSuggestedQuestionClick}
            onDismiss={onDismissSuggestions}
            isLoading={isLoading}
          />
        )}

        {/* When suggestions are hidden, show a minimal clickable line so it doesn't overlap the last message.
            Gated on !isLoading to match the suggestion render conditions — otherwise clicking the link
            during a response flips suggestionsDismissed but the suggestions stay hidden behind !isLoading. */}
        {suggestionsDismissed && onShowSuggestions && !isLoading && (
          <div className="show-suggestions-wrapper">
            <button
              type="button"
              onClick={onShowSuggestions}
              className="show-suggestions-link text-[#2A324B]/60 hover:text-[#2A324B]/80 transition-colors"
            >
              Show suggestions
            </button>
          </div>
        )}
        
        {pendingAttachment && (
          <div className="flex items-center gap-2.5 mb-2 px-3 py-2 bg-white rounded-2xl border border-[#E5E0D8] w-full">
            <img
              src={pendingAttachment.previewUrl}
              alt="Attached photo"
              className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
            />
            <span className="flex-1 min-w-0 text-sm text-[#2A324B] truncate">
              {pendingAttachment.mode === 'face'
                ? 'Selfie attached. Ask something, or just send.'
                : 'Photo attached. Ask something, or just send.'}
            </span>
            <button
              type="button"
              onClick={clearAttachment}
              aria-label="Remove photo"
              className="text-[#2A324B]/50 hover:text-[#2A324B] h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {gateActive ? (
          /* Gated: the composer becomes the ask. They can close the modal and read
             or browse, but they cannot keep chatting without an account — so there
             is no text field to type into, just the CTA that reopens registration.
             No keyboard, so no keyboard-vs-sheet fight. */
          <button
            type="button"
            onClick={() => onGatedCompose && onGatedCompose()}
            className="chat-input-form flex items-center justify-center gap-2 bg-[#1F1310] hover:bg-[#2A1B17] text-white rounded-full px-5 py-3 w-full transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-sm font-semibold">Create a free account to keep chatting</span>
          </button>
        ) : (
        <form onSubmit={handleSubmit} className="chat-input-form flex items-center gap-2 sm:gap-3 bg-white rounded-full px-4 sm:px-5 py-2 sm:py-2.5 border border-[#E5E0D8] w-full">
          <button
            type="button"
            onClick={handleUploadClick}
            className="text-[#2A324B] hover:bg-[#F3D5B5]/20 h-8 w-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
          >
            <HiUpload className="h-4 w-4" />
          </button>
          
         <textarea
          ref={composerRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            // Auto-resize textarea
            const textarea = e.target;
            textarea.style.height = 'auto';
            if (textarea.value.trim() === '') {
              textarea.style.height = '20px'; // Single line when empty
            } else {
              const newHeight = Math.min(textarea.scrollHeight, 120);
              textarea.style.height = newHeight + 'px';
            }
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submitComposer();
              // Input is cleared by sendMessage (text) or the upload (staged photo).
              setTimeout(() => {
                const textarea = e.target;
                if (textarea) {
                  textarea.style.height = 'auto';
                  textarea.style.height = '20px';
                }
              }, 0);
            }
          }}
          onBlur={(e) => {
            // Reset height when focus is lost if empty
            const textarea = e.target;
            if (!textarea.value.trim()) {
              textarea.style.height = '20px';
            }
          }}
          placeholder={pendingAttachment ? "Type your question here, or press Send" : "Type your message..."}
          className="flex-1 border-none bg-transparent text-[#2A324B] placeholder:text-[#2A324B]/60 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none outline-none min-w-0 overflow-hidden"
          style={{ 
            minHeight: '20px',
            maxHeight: '120px',
            lineHeight: '1.4',
            overflow: 'hidden'
          }}
          rows={1}
        />
          
          <button
            type="submit"
          className="bg-[#1F1310] hover:bg-[#2A1B17] text-white rounded-full px-5 h-11 text-xs font-semibold transition-colors uppercase"
            style={{ letterSpacing: '0.12em' }}
          >
            Send
          </button>
        </form>
        )}
        </>)}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          multiple
          accept="image/*"
        />
        {/* Camera-direct input — the `capture` attribute is what makes
            Android jump straight into the camera. Front camera for selfies,
            rear for products. Single image only (camera capture is one shot
            per tap by definition). */}
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*"
          capture={guidanceMode === 'face' ? 'user' : 'environment'}
        />

        {pendingShadePhoto && (
          <ShadePhotoReview
            previewUrl={pendingShadePhoto.previewUrl}
            onUse={usePendingShadePhoto}
            onChooseAgain={clearPendingShadePhoto}
          />
        )}

        {/* Image upload guidance modal */}
        {showImageGuidance && (
          <div
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
            onClick={() => {
              // Guard against ghost-click propagation from the tap that opened
              // the modal (esp. iOS, where the originating touch can travel
              // through the now-unmounted menu and land on this backdrop).
              if (Date.now() - guidanceOpenedAt.current < 400) return;
              setShowImageGuidance(false);
              setSkinAnalysisFromProfile(false);
            }}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div
              className="relative bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full border border-[#F3D5B5]"
              onClick={(e) => e.stopPropagation()}
            >
              {guidanceStep === 'choose' ? (
                <>
                  {/* TWO ENTRY POINTS, NOT A CLASSIFIER. The old sheet asked the user
                      what their photo WAS (skin or product) and then pre-routed on that
                      answer, so a mis-tap silently produced the wrong read. Now the user
                      says what they want to DO: the guided scan is its own first-class
                      flow, and everything else is one generic upload the model routes
                      from the photo itself. */}
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-[#341C1C]">Add a photo</h3>
                    <p className="text-[13px] text-[#2A324B]/50 mt-0.5">Whatever it is, Son:mi takes it from here.</p>
                  </div>

                  <div className="space-y-2.5">
                    <button
                      onClick={() => { setGuidanceMode('face'); setGuidanceStep('scanTips'); }}
                      className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl border-2 border-[#C8956A] bg-white text-left active:scale-[0.99] transition-transform"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#F3D5B5] flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-[#8A5A2B]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] font-semibold text-[#341C1C]">{scanIntakeKnown ? 'Skin check-in' : 'Scan my skin'}</p>
                        <p className="text-[12.5px] leading-snug text-[#2A324B]/55">Guided, whole-face. Tracks changes over time</p>
                      </div>
                    </button>

                    <button
                      onClick={() => { setGuidanceMode('photo'); handleProceedToUpload('library', 'photo'); }}
                      className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl border border-[#341C1C]/12 bg-white text-left active:scale-[0.99] transition-transform"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#EFEAE3] flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-[#5F5A54]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 8.25h.008v.008H18V8.25zm2.25 10.5H3.75A1.5 1.5 0 012.25 17.25V6.75a1.5 1.5 0 011.5-1.5h16.5a1.5 1.5 0 011.5 1.5v10.5a1.5 1.5 0 01-1.5 1.5z" /></svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] font-semibold text-[#341C1C]">Upload a photo</p>
                        <p className="text-[12.5px] leading-snug text-[#2A324B]/55">Product, swatch, makeup or a skin question</p>
                      </div>
                    </button>
                  </div>

                  <p className="text-center text-[12px] text-[#2A324B]/45 mt-3.5 mb-1">Not sure? Upload it and Son:mi works out what it is.</p>

                  <button
                    onClick={() => { setShowImageGuidance(false); setSkinAnalysisFromProfile(false); }}
                    className="w-full py-2.5 text-[13px] text-[#2A324B]/55 hover:text-[#2A324B] transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {/* Guided scan prep, unchanged: this is the premium flow, so it
                      keeps its tips, its front camera and its intake. */}
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#F2EAE1] flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#341C1C]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" /></svg>
                    </div>
                    <h3 className="text-base font-semibold text-[#341C1C]">Scan my skin</h3>
                    <p className="text-[13px] text-[#2A324B]/50 mt-0.5">A few seconds, and we track it over time</p>
                  </div>

                  <ul className="space-y-3 mb-5">
                    <li className="flex items-start gap-3"><div className="w-8 h-8 rounded-full bg-[#F2EAE1] flex items-center justify-center flex-shrink-0 mt-0.5"><svg className="w-3.5 h-3.5 text-[#341C1C]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg></div><div><p className="text-sm font-medium text-[#341C1C]">Natural light, face the window</p><p className="text-[13px] text-[#2A324B]/55">Soft daylight, avoid shadows across your face</p></div></li>
                    <li className="flex items-start gap-3"><div className="w-8 h-8 rounded-full bg-[#F2EAE1] flex items-center justify-center flex-shrink-0 mt-0.5"><svg className="w-3.5 h-3.5 text-[#341C1C]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" /></svg></div><div><p className="text-sm font-medium text-[#341C1C]">Clean, bare skin if possible</p><p className="text-[13px] text-[#2A324B]/55">Your natural skin gives us the most accurate read</p></div></li>
                    <li className="flex items-start gap-3"><div className="w-8 h-8 rounded-full bg-[#F2EAE1] flex items-center justify-center flex-shrink-0 mt-0.5"><svg className="w-3.5 h-3.5 text-[#341C1C]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" /></svg></div><div><p className="text-sm font-medium text-[#341C1C]">Full face, straight-on</p><p className="text-[13px] text-[#2A324B]/55">Look directly at the camera, whole face in frame</p></div></li>
                  </ul>
                  <p className="text-center text-[13px] text-[#2A324B]/40 mb-5">Any clear selfie works, don&rsquo;t stress it</p>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleProceedToUpload('camera', 'face')}
                      className="w-full py-2.5 rounded-full bg-[#341C1C] text-white text-sm font-semibold hover:bg-[#5a3030] transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                      </svg>
                      Start scan
                    </button>
                    <button
                      onClick={() => handleProceedToUpload('library', 'face')}
                      className="w-full py-2.5 rounded-full border border-[#341C1C]/20 text-sm font-semibold text-[#341C1C] hover:bg-[#F2EAE1] transition-colors"
                    >
                      Choose from library
                    </button>
                    <button
                      onClick={() => setGuidanceStep('choose')}
                      className="w-full py-2 text-[13px] text-[#2A324B]/55 hover:text-[#2A324B] transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Guided skin-capture overlay (flag-gated). Produces a JPEG File and
          hands it to the SAME uploadImage path a library/camera pick uses, so
          the existing consent + compression + analysis pipeline is unchanged. */}
      {/* Two quick questions between the scan and the results. Whatever the
          user answers is threaded into the SAME analysis call. */}
      <ScanIntakeModal
        isOpen={!!pendingScan}
        onSubmit={(intake) => submitPendingScan(intake)}
        onSkip={() => submitPendingScan(null)}
      />

      {showGuidedCapture && (
        <Suspense fallback={null}>
          <GuidedCapture
            onCapture={(files) => {
              const arr = Array.isArray(files) ? files : [files];
              // Commit the visible handoff before dismissing the full-screen
              // camera. This prevents the previous chooser from flashing back
              // while scan files are being prepared.
              setLoadingPurpose(IMAGE_ROLES.FULL_SKIN_SCAN);
              setScanHandoff(true);
              setShowGuidedCapture(false);
              trackPhotoUpload({ source: 'guided', kind: 'skin' });
              // The straight-on shot (arr[0]) is the card hero; ALL angles (front +
              // both cheeks + chin) go to the backend for the multi-angle read, so
              // the analysis uses the whole scan, not just the front.
              // Hold here and ask the two intake questions first — the answers
              // ride along in the same analysis call.
              beginScan(arr[0], arr);
            }}
            onCancel={() => {
              setShowGuidedCapture(false);
              setSkinAnalysisFromProfile(false);
            }}
            onError={() => { /* GuidedCapture shows its own error + Go back */ }}
          />
        </Suspense>
      )}

      {/* Chat History Drawer */}
      <ChatHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        chatList={chatList}
        currentSessionId={currentSessionId}
        onNewChat={onNewChat}
        onSwitchChat={onSwitchChat}
      />

      <ClimateModal
        isOpen={showClimateModal}
        isAnonymous={isAnonymous}
        onSave={saveClimate}
        onCreateAccount={() => { closeClimateModal(); onShowSignIn?.(); }}
        onSignIn={() => { trackClimateSignInClicked(); closeClimateModal(); onShowSignIn?.(); }}
        onClose={closeClimateModal}
      />

      {/* Menu Sheet */}
      <MenuSheet
        key={menuInitialView}
        isOpen={isMenuOpen}
        initialView={menuInitialView}
        onClose={() => { setIsMenuOpen(false); setMenuInitialView('menu'); }}
        onNavigate={handleNavigate}
        savedProducts={savedProducts}
        saveProduct={saveProduct}
        updateProduct={updateProduct}
        deleteProduct={deleteProduct}
        customListNames={customListNames}
        createCustomList={createCustomList}
        renameCustomList={renameCustomList}
        onLoadProducts={async () => {
          trackProductCatalogViewed(); // Track product catalog viewed
          if (!userId) return;
          // Skip refetch if we loaded within the TTL (preloadSavedProducts
          // ran on mount; menu button doesn't need to redo it on every tap).
          const sinceLastFetch = Date.now() - lastSavedProductsFetchRef.current;
          if (sinceLastFetch < SAVED_PRODUCTS_TTL_MS) {
            return;
          }
          const products = await getSavedProductsFromBackend(userId);
          if (products) {
            const refreshed = await refreshProductImages(products);
            setSavedProducts(refreshed);
            lastSavedProductsFetchRef.current = Date.now();
          }
        }}
        deleteCustomList={deleteCustomList}
        onContinueToChat={handleContinueToChat}
        isAnonymous={isAnonymous}
        onShowSignIn={onShowSignIn}
        onSignInDirect={onPersistentSignIn}
        onGatedAction={onGatedCompose}
        userId={userId}
        onImageConsentChange={onImageConsentChange}
        onProfileSave={onProfileSave}
        hasImageConsent={!!imageConsent}
        requestImageConsent={requestConsent}
      />
      {/* Profile Completion Modal */}
      {showProfileNudge && !isAnonymous && (
        <ProfileCompletionNudge
          completeness={profileCompleteness}
          missingFields={missingFields}
          onCompleteProfile={() => {
            if (onCompleteProfile) onCompleteProfile();
            setMenuInitialView('personal-profile');
            setIsMenuOpen(true);
          }}
          onDismiss={onProfileNudgeDismiss}
        />
      )}

      {/* Drag & Drop Overlay */}
      {isDragOver && (
        <div className="fixed inset-0 bg-[#F3D5B5]/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-dashed border-[#F3D5B5]">
            <div className="text-center">
              <svg className="h-16 w-16 text-[#F3D5B5] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <h3 className="text-lg font-semibold text-[#2A324B] mb-2">Drop images here</h3>
            <p className="text-[#2A324B]/70 mb-3">Upload images of your skin, products, or skincare routine for intelligent analysis</p>
            <div className="text-xs text-[#2A324B]/60 space-y-1">
              <p>💡 <strong>Pro tip:</strong> Mix different types - skin photos + products for personalised recommendations</p>
              <p>🤖 <strong>Smart AI:</strong> I'll analyse your images and ask follow-up questions if needed</p>
            </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Product Save From Analysis Modal */}
      {showProductSaveFromAnalysis && (
        <ProductSaveFromAnalysis
          analysisResult={currentAnalysisForSave}
          productImage={currentProductImageForSave}
          onSave={handleSaveProductFromAnalysis}
          onCancel={() => setShowProductSaveFromAnalysis(false)}
        />
      )}
      </div>{/* End desktop wrapper */}
    </div>
  );
};

export default ChatUI;
